import { CALENDAR_VIEW_DATE_TYPE } from 'typings/models/calendar/dateType.enum';
import { ORDER_TYPE_TECHNICAL_NAME } from 'typings/models/order/orderType.enum';
import { TechnicianVisitIntervalResponseDTO } from 'typings/dto/calendarVisitInterval';
import { ORDER_FILTER } from 'typings/models/order/orderFilter.enum';
import { CALENDAR_RESOURCE_TYPE } from 'typings/models/calendar/resource.enum';
import { CustomEventInput } from 'typings/models/calendar/_calendar';
import EntityUtils from 'utils/Entity';

export default class CalendarUtils {
  private static viewTypeMap = {
    day: 'resourceTimelineDay',
    week: 'resourceTimelineWeek',
  };
  public static getViewFullName(view: string) {
    return this.viewTypeMap[view as CALENDAR_VIEW_DATE_TYPE];
  }

  private static colorMapByOrderType: Record<ORDER_TYPE_TECHNICAL_NAME, Record<'backgroundColor' | 'borderColor' | 'textColor', string>> = {
    default: {
      backgroundColor: '#C8E4FF',
      borderColor: '#1890FF52',
      textColor: '#0C53B7',
    },
    measure: {
      backgroundColor: '#D6F5CD',
      borderColor: '#00AB5552',
      textColor: '#229A16',
    },
    reclamation: {
      backgroundColor: '#FFF0C4',
      borderColor: '#FFC10752',
      textColor: '#B78103',
    },
    warranty: {
      backgroundColor: '#E5E1FF',
      borderColor: '#906CBD52',
      textColor: '#5D4988',
    },
  };

  public static getColorByOrderType = (orderType: ORDER_TYPE_TECHNICAL_NAME) => {
    return this.colorMapByOrderType[orderType];
  };

  public static mapVisitIntervalsToDate<T extends { start: string; end: string }>(visitIntervals: T[]) {
    return visitIntervals.map((visit) => ({ ...visit, start: new Date(visit.start), end: new Date(visit.end) }));
  }

  public static mapVisitIntervalsToISOString<T extends { from: Date; to: Date }>(visitIntervals: T[]) {
    return visitIntervals.map((visit) => ({ ...visit, from: visit.from.toISOString, to: visit.to.toISOString }));
  }

  public static mapTechniciansVisitIntervalToEventsArray = (visitIntervals: TechnicianVisitIntervalResponseDTO[]) => {
    return visitIntervals.map((visit) => {
      let start = new Date(visit.start);
      start.setHours(start.getUTCHours());
      let end = new Date(visit.end);
      end.setHours(end.getUTCHours());
      return {
        ...visit,
        id: visit.start + visit.end,
        editable: false,
        droppable: false,
        start,
        from: start,
        end,
        to: end,
      };
    });
  };

  public static translateTechniciansVisitIntervalMapToEventsArray = (
    techniciansMap: Record<string, TechnicianVisitIntervalResponseDTO[]>
  ) => {
    const keys = Object.keys(techniciansMap);

    return keys.flatMap((key) => {
      return techniciansMap[key].map((visit) => {
        let start = new Date(visit.start);
        start.setHours(start.getUTCHours());
        let end = new Date(visit.end);
        end.setHours(end.getUTCHours());
        return {
          ...visit,
          id: visit.start + visit.end,
          editable: false,
          droppable: false,
          start,
          from: start,
          end,
          to: end,
          resourceIds: [key],
        };
      });
    });
  };

  public static filterFormOrderVisitIntervals<T extends { orderId?: string }>(visitIntervals: T[]) {
    return visitIntervals.filter((visitInterval) => !visitInterval.orderId);
  }

  public static getVisitIntervalsFromOrders(orders: Order[]) {
    return orders.flatMap((order) => this.getVisitIntervalsFromOrder(order));
  }

  public static createResourceEventsFromVisitInterval(visit: CalendarVisitInterval): CustomEventInput[] {
    const result: CustomEventInput[] = [];
    const resourceIds = visit.technicianTeam ? [visit.technicianTeam.id] : visit.serviceProvider ? [visit.serviceProvider.id] : [];
    visit.technicians?.forEach((technician) => resourceIds.push(technician.id));
    const createEvent = (
      resource: Order['technicianTeam'] | Order['serviceProvider'] | Order['technicians'][number],
      providerType: CALENDAR_RESOURCE_TYPE
    ) => ({
      ...visit,
      id: visit.id,
      groupId: visit.id,
      resourceId: resource?.id,
      start: visit.from,
      end: visit.to,
      providerType,
    });
    visit.technicianTeam && result.push(createEvent(visit.technicianTeam, CALENDAR_RESOURCE_TYPE.technicianTeam));
    visit.serviceProvider && result.push(createEvent(visit.serviceProvider, CALENDAR_RESOURCE_TYPE.serviceProvider));
    visit.technicians.forEach((technician) => result.push(createEvent(technician, CALENDAR_RESOURCE_TYPE.technician)));
    return result;
  }

  /** используется для маппинга визитов в события календаря установщика */
  public static createEventFromVisitInterval(visit: CalendarVisitInterval): CustomEventInput {
    return {
      ...visit,
      id: visit.id,
      groupId: visit.id,
      start: visit.from,
      end: visit.to,
    };
  }

  public static getVisitIntervalsFromOrder(order: Order): CalendarVisitInterval[] {
    return order.visitDateIntervals.flatMap((visit) => {
      return {
        ...visit,
        visitDateIntervals: order.visitDateIntervals,
        createdAt: order.createdAt,
        updatedAt: order.updatedAt,
        createdAtNumber: order.createdAt.getTime(),
        updatedAtNumber: order.updatedAt.getTime(),
        numberOfTechnicians: order.numberOfTechnicians,
        id: visit.id,
        region: order.region,
        technicianTeam: order.technicianTeam,
        technicians: order.technicians,
        serviceProvider: order.serviceProvider,
        orderTimezone: order.region.timezone,
        orderType: order.type,
        orderId: order.id,
        orderNumber: order.number,
        orderStatus: order.status,
      };
    });
  }

  public static createEventsFromOrder(order: Order) {
    return this.getVisitIntervalsFromOrder(order).flatMap(this.createResourceEventsFromVisitInterval);
  }

  public static getFilterValueText = (
    locationSearchObject: LocationSearchObject,
    departments: EntityWithName[],
    providersType: EntityWithName[],
    orderTypes: EntityWithName[],
    paymentStatuses: EntityWithName[]
  ) => {
    const providerIds = locationSearchObject[ORDER_FILTER.providerType]?.values ?? [];
    const departmentIds = locationSearchObject[ORDER_FILTER.department]?.values ?? [];
    const orderTypeIds = locationSearchObject[ORDER_FILTER.type]?.values ?? [];
    const paymentStatus = locationSearchObject[ORDER_FILTER.serviceItemPaymentStatus]?.values ?? [];
    if (providerIds.length === 0 && departmentIds.length === 0 && orderTypeIds.length === 0 && paymentStatus.length === 0) {
      return '';
    }
    const providersPart = EntityUtils.getByIds(providerIds, providersType)
      .map((p) => p.name)
      .join(', ');
    const departmentsPart = EntityUtils.getByIds(departmentIds, departments)
      .map((d) => d.name)
      .join(', ');
    const orderTypesPart = EntityUtils.getByIds(orderTypeIds, orderTypes)
      .map((ot) => ot.name)
      .join(', ');

    const paymentStatusPart = EntityUtils.getByIds(paymentStatus, paymentStatuses)
      .map((ps) => ps.name)
      .join(', ');

    const result: string[] = [];
    providersPart && result.push(providersPart);
    departmentsPart && result.push(departmentsPart);
    orderTypesPart && result.push(orderTypesPart);
    paymentStatusPart && result.push(paymentStatusPart);

    return result.join('; ');
  };

  public static getVisitProviderType = (visit: CalendarVisitInterval) => {
    if (visit.technicianTeam) {
      return CALENDAR_RESOURCE_TYPE.technicianTeam;
    }
    if (visit.serviceProvider) {
      return CALENDAR_RESOURCE_TYPE.serviceProvider;
    }
    return CALENDAR_RESOURCE_TYPE.technician;
  };
}
